/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Component, HostListener, OnDestroy, ViewEncapsulation } from '@angular/core';
import { LanguageService, LogService, NavigationService, SocketService, ThemeService } from './core/services';
import { NavigationEnd, Router } from '@angular/router';
import { environment, ModulesEnum } from '../environments/environment';

@Component({
    selector: 'app-core',
    encapsulation: ViewEncapsulation.None,
    templateUrl: './app.component.html',
    styleUrls: []
})

export class AppComponent implements OnDestroy {
    /**
     * Store is loading state.
     */
    public isLoading: boolean = true;

    /**
     * Store the environment name for mobile overlay.
     */
    public environmentName: string;

    constructor(
        private languageService: LanguageService,
        private logService: LogService,
        private navigationService: NavigationService,
        private router: Router,
        private socketService: SocketService,
        private themeService: ThemeService
    ) {

        // Enable logging
        this.logService.toggle();
        // Set application environment
        this.logService.setEnvironment(environment.production);

        this.setScriptsInHeader();
        this.setFavicon();
        this.setDocumentTitle();
        this.setEnvironmentName();
        this.setThemeInService();
        this.setLanguage();
        this.storeNavigationHistory();
    }

    /**
     * Get the Modules ENUM, so we can use it in the view.
     */
    get modulesEnum(): any {
        return ModulesEnum;
    }

    @HostListener('window:beforeunload')
    public async ngOnDestroy() {
        this.socketService.close();
    }

    /**
     * On Activate
     */
    public onActivate(): void {
        // you have access to the component instance
        this.isLoading = false;
    }

    /**
     * Set script in the header based on module.
     * @private
     */
    private setScriptsInHeader(): void {
        const scripts: string[] = [
            '/assets/shared/libs/algoliasearch-lite.umd.js',
            '/assets/shared/libs/instantsearch.production.min.js'
        ];

        if (environment.module === ModulesEnum.CENTRALISED_MANAGEMENT) {
            const portalScripts: string[] = [
                'https://js.stripe.com/v3/',
                'https://assets.calendly.com/assets/external/widget.js',
                '/assets/shared/libs/d3.min.js'
            ];

            scripts.push(...portalScripts);

            this.appendGoogleTagManager();
        }

        this.appendScripts(scripts);
        this.appendIntercomChat();
    }

    /**
     * Append Scripts
     * @param scripts
     * @private
     */
    private appendScripts(scripts: string[]): void {
        for (const scriptSrc of scripts) {
            const script: HTMLScriptElement = document.createElement('script');
            script.src = scriptSrc;
            document.head.appendChild(script);
        }
    }

    /**
     * Append google tag manager.
     * @private
     */
    private appendGoogleTagManager(): void {
        const script: HTMLScriptElement = document.createElement('script');
        script.id = 'google-tag-manager';
        script.text = `
             (function (w, d, s, l, i) {
                w[l] = w[l] || [];
                w[l].push({
                    'gtm.start':
                        new Date().getTime(), event: 'gtm.js'
                });

                // eslint-disable-next-line prefer-const
                let f = d.getElementsByTagName(s)[0],
                    // eslint-disable-next-line prefer-const,eqeqeq
                    j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : '';
                // @ts-ignore
                j.async = true;
                // @ts-ignore
                j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
                f.parentNode.insertBefore(j, f);
            })(window, document, 'script', 'dataLayer', 'GTM-WG3QK4D');
            `;
        document.head.appendChild(script);
    }

    /**
     * Append intercom chat.
     * @private
     */
    private appendIntercomChat(): void {
        const settings: HTMLScriptElement = document.createElement('script');
        settings.text = `
            window.intercomSettings = {
                api_base: "https://api-iam.intercom.io",
                app_id: "su2x8ti6",
                action_color: '#4a0096',
                background_color: '#af00ff',
                custom_launcher_selector: '#open-intercom'
            };
        `;
        document.body.appendChild(settings);

        const script: HTMLScriptElement = document.createElement('script');
        script.id = 'intercomChat';
        script.text = `
            (function () {
                var w = window;
                var ic = w.Intercom;
                if (typeof ic === "function") {
                    ic('reattach_activator');
                    ic('update', w.intercomSettings);
                } else {
                    var d = document;
                    var i = function () {
                        i.c(arguments);
                    };
                    i.q = [];
                    i.c = function (args) {
                        i.q.push(args);
                    };
                    w.Intercom = i;
                    var l = function () {
                        var s = d.createElement('script');
                        s.type = 'text/javascript';
                        s.async = true;
                        s.src = 'https://widget.intercom.io/widget/su2x8ti6';
                        var x = d.getElementsByTagName('script')[0];
                        x.parentNode.insertBefore(s, x);
                    };
                    if (document.readyState === 'complete') {
                        l();
                    } else if (w.attachEvent) {
                        w.attachEvent('onload', l);
                    } else {
                        w.addEventListener('load', l, false);
                    }
                }
            })();
            `;
        document.head.appendChild(script);
    }

    /**
     * Set Favicon.
     * @private
     */
    private setFavicon(): void {
        const favicon: HTMLElement = document.getElementById('appFavicon');
        favicon?.setAttribute('href', '/assets/shared/logos/favicon.svg');
    }

    /**
     * Get the document title.
     * @private
     */
    private setDocumentTitle(): void {
        let documentTitle: string = '';
        switch (environment.module) {
            case this.modulesEnum.CENTRALISED_MANAGEMENT:
                documentTitle = 'Loadbalancer Portal';
                break;
            case this.modulesEnum.LOADBALANCER:
                documentTitle = 'Loadbalancer Endurance';
                break;
            case this.modulesEnum.DEEPTHOUGHT:
                documentTitle = 'Loadbalancer Admin';
        }

        document.title = documentTitle;
    }

    /**
     * Get environment name for mobile overlay.
     * @private
     */
    private setEnvironmentName(): void {
        let environmentName: string = '';
        switch (this.modulesEnum[environment.module]) {
            case this.modulesEnum.CENTRALISED_MANAGEMENT:
                environmentName = 'Portal';
                break;
            case this.modulesEnum.LOADBALANCER:
                environmentName = 'Endurance';
                break;
            case this.modulesEnum.DEEPTHOUGHT:
                environmentName = 'Deep Thought';
        }

        this.environmentName = environmentName;
    }

    /**
     * Set theme in theme service.
     * @private
     */
    private setThemeInService(): void {
        // If a theme exists in localstorage set that otherwise set the environment theme
        const lbTheme: string = localStorage.getItem('lbTheme');
        if (lbTheme && lbTheme !== 'undefined') {
            this.themeService.setTheme(JSON.parse(lbTheme).name);
        } else {
            this.themeService.setTheme(environment.theme);
        }
    }

    /**
     * Set language.
     * @private
     */
    private setLanguage(): void {
        const location: string = navigator.language || 'en-us';
        // Set userManagement default language
        this.languageService.setLanguage(location);
    }

    /**
     * Store navigation history.
     * @private
     */
    private storeNavigationHistory(): void {
        this.router.events
            .subscribe({
                next: (event: any): void => {
                    if (event instanceof NavigationEnd) {
                        this.navigationService.history = event.urlAfterRedirects;
                    }
                }
            });
    }
}
